

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import axios from "axios";
import "react-quill/dist/quill.snow.css";
import { BASE_URL } from "../../services/configUrls";

// ==============================================================
// AUTH
// ==============================================================
const getAccessToken = () => localStorage.getItem("accessToken");

const getAuthConfig = () => {
  const token = getAccessToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// ==============================================================
// CONSTANTS + HELPERS
// ==============================================================
// const SERIF =
//   '"Source Serif 4", "Linux Libertine", Georgia, "Times New Roman", serif';
const SERIF = '"Roboto", sans-serif';
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const LAYOUT_SX = {
  display: "grid",
  gap: 4,
  alignItems: "start",
  gridTemplateColumns: { xs: "1fr", md: "290px minmax(0, 1fr)" },
};

const HIDDEN_SCROLLBAR_SX = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

const getTokens = (dark) => {
  const t = {
    page: dark ? "#171923" : "#f8fafc",
    card: dark ? "#1a202c" : "#ffffff",
    subtle: dark ? "#171923" : "#f8fafc",
    border: dark ? "#2d3748" : "#e2e8f0",
    text: dark ? "#e2e8f0" : "#1a202c",
    text2: dark ? "#cbd5e0" : "#374151",
    muted: dark ? "#a0aec0" : "#718096",
    accent: dark ? "#63b3ed" : "#2563eb",
    accentBg: dark ? "rgba(99,179,237,0.14)" : "#eff6ff",
    line: dark ? "#4a5568" : "#a2a9b1",
    codeBg: dark ? "#2d3748" : "#eaecf0",
  };
  t.bd = `1px solid ${t.border}`;
  return t;
};

const sanitizeGuideHtml = (html = "") => {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const decodedHtml =
    parsed.body.children.length === 0 &&
    /&lt;\/?[a-z][\s\S]*?&gt;/i.test(parsed.body.textContent || "")
      ? parsed.body.textContent
      : html;
  const doc = new DOMParser().parseFromString(decodedHtml, "text/html");
  doc
    .querySelectorAll("script,style,iframe,object,embed,link,meta")
    .forEach((node) => node.remove());
  doc.body.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      if (
        /^on/i.test(attribute.name) ||
        /^\s*javascript:/i.test(attribute.value)
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });
  return doc.body.innerHTML;
};

// Sanitizes the guide HTML and checks whether it has any text.
const parseGuide = (html = "") => {
  const doc = new DOMParser().parseFromString(
    sanitizeGuideHtml(html),
    "text/html",
  );

  return {
    html: doc.body.innerHTML,
    hasText: Boolean((doc.body.textContent || "").trim()),
  };
};

const apiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return (
      detail
        .map((item) => (typeof item === "string" ? item : item?.msg))
        .filter(Boolean)
        .join(", ") || fallback
    );
  }
  return error?.message || fallback;
};

const mapOptions = (rows) =>
  (Array.isArray(rows) ? rows : []).map((option) => ({
    id: option.id,
    name: option.option_name || "",
    description: option.description || "",
    guides: (Array.isArray(option.guides) ? option.guides : []).map(
      (guide) => ({
        id: guide.id,
        title: guide.title || "",
      }),
    ),
  }));

const mapGuide = (g) => ({
  id: g.id,
  title: g.title || "",
  text: g.text || "",
  images: Array.isArray(g.image) ? g.image : g.image ? [g.image] : [],
  updatedAt: g.updated_at || g.created_at || "",
});

// ==============================================================
// ARTICLE CONTENT STYLES (the rich text inside a guide)
// ==============================================================
// const getArticleSx = (t) => ({
//   fontFamily: SERIF,
//   fontSize: { xs: 16, md: 17 },
//   lineHeight: 1.75,
//   color: t.text,
//   overflowWrap: "anywhere",
//   "& p": { margin: "0 0 1.1em" },
//   "& h1, & h2": {
//     display: "flow-root",
//     fontFamily: SERIF,
//     fontWeight: 400,
//     fontSize: { xs: 24, md: 28 },
//     lineHeight: 1.3,
//     margin: "1.5em 0 0.7em",
//     paddingBottom: "0.2em",
//     borderBottom: `1px solid ${t.line}`,
//     color: t.text,
//     scrollMarginTop: "24px",
//   },
//   "& h3, & h4": {
//     fontFamily: SERIF,
//     fontWeight: 700,
//     fontSize: { xs: 18, md: 20 },
//     lineHeight: 1.4,
//     margin: "1.3em 0 0.5em",
//     color: t.text,
//   },
//   "& > :first-child": { marginTop: 0 },
//   "& a": { color: t.accent, textDecoration: "none" },
//   "& a:hover": { textDecoration: "underline" },
//   "& sup": { fontSize: "0.72em", color: t.accent, lineHeight: 0 },
//   "& ul, & ol": {
//     margin: "0 0 1.1em",
//     paddingLeft: "1.6em",
//     listStylePosition: "outside",
//   },
//   "& ul": { listStyleType: "disc" },
//   "& ol": { listStyleType: "decimal" },
//   "& li": { marginBottom: "0.35em" },
//   "& blockquote": {
//     margin: "0 0 1.1em",
//     padding: "0.25em 0 0.25em 1em",
//     borderLeft: `3px solid ${t.accent}`,
//     color: t.muted,
//   },
//   "& pre": {
//     margin: "0 0 1.1em",
//     padding: "12px 14px",
//     overflowX: "auto",
//     borderRadius: "6px",
//     border: t.bd,
//     backgroundColor: t.subtle,
//     fontFamily: MONO,
//     fontSize: 13.5,
//     lineHeight: 1.6,
//   },
//   "& code": {
//     fontFamily: MONO,
//     fontSize: "0.88em",
//     padding: "1px 5px",
//     borderRadius: "4px",
//     backgroundColor: t.codeBg,
//   },
//   "& pre code": { padding: 0, backgroundColor: "transparent" },
//   "& table": { borderCollapse: "collapse", margin: "0 0 1.1em" },
//   "& th, & td": { border: `1px solid ${t.line}`, padding: "6px 10px" },
//   "& img": { maxWidth: "100%", height: "auto" },
//   // Quill alignment / indent classes
//   "& .ql-align-center": { textAlign: "center" },
//   "& .ql-align-right": { textAlign: "right" },
//   "& .ql-align-justify": { textAlign: "justify" },
//   "& .ql-indent-1": { paddingLeft: "3em" },
//   "& .ql-indent-2": { paddingLeft: "6em" },
//   "& .ql-indent-3": { paddingLeft: "9em" },
// });

// const getArticleSx = (t) => ({
//   fontFamily: SERIF,
//   fontSize: { xs: 16, md: 17 },
//   lineHeight: 1.75,
//   color: t.text,
//   overflowWrap: "anywhere",

//   "& p": {
//     margin: "0 0 1.1em",
//   },

//   "& h1, & h2": {
//     display: "flow-root",
//     fontFamily: SERIF,
//     fontWeight: 400,
//     fontSize: { xs: 24, md: 28 },
//     lineHeight: 1.3,
//     margin: "1.5em 0 0.7em",
//     paddingBottom: "0.2em",
//     borderBottom: `1px solid ${t.line}`,
//     color: t.text,
//     scrollMarginTop: "24px",
//   },

//   "& h3, & h4": {
//     fontFamily: SERIF,
//     fontWeight: 700,
//     fontSize: { xs: 18, md: 20 },
//     lineHeight: 1.4,
//     margin: "1.3em 0 0.5em",
//     color: t.text,
//   },

//   "& > :first-child": {
//     marginTop: 0,
//   },

//   "& a": {
//     color: t.accent,
//     textDecoration: "none",
//   },

//   "& a:hover": {
//     textDecoration: "underline",
//   },

//   "& sup": {
//     fontSize: "0.72em",
//     color: t.accent,
//     lineHeight: 0,
//   },

//   "& ul, & ol": {
//     margin: "0 0 1.1em",
//     paddingLeft: "1.6em",
//     listStylePosition: "outside",
//   },

//   "& ul": {
//     listStyleType: "disc",
//   },

//   "& ol": {
//     listStyleType: "decimal",
//   },

//   "& li": {
//     marginBottom: "0.35em",
//   },

//   "& blockquote": {
//     margin: "0 0 1.1em",
//     padding: "0.25em 0 0.25em 1em",
//     borderLeft: `3px solid ${t.accent}`,
//     color: t.muted,
//   },

//   "& pre": {
//     margin: "0 0 1.1em",
//     padding: "12px 14px",
//     overflowX: "auto",
//     borderRadius: "6px",
//     border: t.bd,
//     backgroundColor: t.subtle,
//     fontFamily: MONO,
//     fontSize: 13.5,
//     lineHeight: 1.6,
//   },

//   "& code": {
//     fontFamily: MONO,
//     fontSize: "0.88em",
//     padding: "1px 5px",
//     borderRadius: "4px",
//     backgroundColor: t.codeBg,
//   },

//   "& pre code": {
//     padding: 0,
//     backgroundColor: "transparent",
//   },

//   "& table": {
//     borderCollapse: "collapse",
//     margin: "0 0 1.1em",
//   },

//   "& th, & td": {
//     border: `1px solid ${t.line}`,
//     padding: "6px 10px",
//   },

//   "& img": {
//     maxWidth: "100%",
//     height: "auto",
//   },

 
// });

const getArticleSx = (t) => ({
  fontFamily: SERIF,
  fontSize: { xs: 16, md: 17 },
  lineHeight: 1.75,
  color: t.text,
  overflowWrap: "anywhere",

  "& p": {
    margin: "0 0 1.1em",
  },

  "& h1, & h2": {
    display: "flow-root",
    fontFamily: SERIF,
    fontWeight: 400,
    fontSize: { xs: 24, md: 28 },
    lineHeight: 1.3,
    margin: "1.5em 0 0.7em",
    paddingBottom: "0.2em",
    borderBottom: `1px solid ${t.line}`,
    color: t.text,
    scrollMarginTop: "24px",
  },

  "& h3, & h4": {
    fontFamily: SERIF,
    fontWeight: 700,
    fontSize: { xs: 18, md: 20 },
    lineHeight: 1.4,
    margin: "1.3em 0 0.5em",
    color: t.text,
  },

  "& > :first-child": {
    marginTop: 0,
  },

  "& a": {
    color: t.accent,
    textDecoration: "none",
  },

  "& a:hover": {
    textDecoration: "underline",
  },

  "& sup": {
    fontSize: "0.72em",
    color: t.accent,
    lineHeight: 0,
  },

  "& ul, & ol": {
    margin: "0 0 1.1em",
    paddingLeft: "1.6em",
    listStylePosition: "outside",
  },

  "& ul": {
    listStyleType: "disc",
  },

  "& ol": {
    listStyleType: "decimal",
  },

  "& li": {
    marginBottom: "0.35em",
  },

  "& blockquote": {
    margin: "0 0 1.1em",
    padding: "0.25em 0 0.25em 1em",
    borderLeft: `3px solid ${t.accent}`,
    color: t.muted,
  },

  // Code block: always dark, in both light and dark mode
  "& pre, & pre.ql-syntax": {
    margin: "0 0 1.1em",
    padding: "12px 14px",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "#23241f",
    color: "#f8f8f2",
    fontFamily: MONO,
    fontSize: 13.5,
    lineHeight: 1.6,
  },

  // Inline code (not inside a code block)
  "& code": {
    fontFamily: MONO,
    fontSize: "0.88em",
    padding: "1px 5px",
    borderRadius: "4px",
    backgroundColor: t.codeBg,
  },

  // Code inside a dark block: no extra background or padding
  "& pre code": {
    padding: 0,
    backgroundColor: "transparent",
    color: "inherit",
  },

  "& table": {
    borderCollapse: "collapse",
    margin: "0 0 1.1em",
  },

  "& th, & td": {
    border: `1px solid ${t.line}`,
    padding: "6px 10px",
  },

  "& img": {
    maxWidth: "100%",
    height: "auto",
  },
});

// ==============================================================
// SMALL SHARED UI PIECES
// ==============================================================
const EmptyState = ({ t, icon, title, hint, action }) => (
  <Box sx={{ py: 8, px: 2, textAlign: "center" }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        mx: "auto",
        mb: 2,
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: t.accent,
        backgroundColor: t.accentBg,
      }}
    >
      <Icon icon={icon} width={28} />
    </Box>
    <Typography sx={{ fontWeight: 600, fontSize: 16, color: t.text }}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{ mt: 0.5, mx: "auto", maxWidth: 380, color: t.muted }}
    >
      {hint}
    </Typography>
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

const RetryButton = ({ onClick }) => (
  <Button
    variant="outlined"
    startIcon={<Icon icon="lucide:refresh-cw" width={16} />}
    onClick={onClick}
    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
  >
    Try again
  </Button>
);

// ==============================================================
// SKELETONS (mirror the real layout)
// ==============================================================

// One paragraph of placeholder text lines (same line height as the article)
const SkeletonParagraph = ({ widths }) => (
  <Box sx={{ mb: "1.1em" }}>
    {widths.map((w, i) => (
      <Box
        key={i}
        sx={{ height: "1.75em", display: "flex", alignItems: "center" }}
      >
        <Skeleton
          variant="rounded"
          animation="wave"
          width={`${w}%`}
          height="0.65em"
        />
      </Box>
    ))}
  </Box>
);

// Mirrors the real article: image carousel on top, then one paragraph
const GALLERY_CONTROL_SLOT_SX = {
  flexShrink: 0,
  width: 40,
  height: 40,
};

const ArticleBodySkeleton = ({ t }) => (
  <Box sx={{ fontSize: { xs: 16, md: 17 } }}>
    <Box
      aria-hidden="true"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        mb: 3,
      }}
    >
      <Box sx={GALLERY_CONTROL_SLOT_SX} />
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flex: 1,
          minWidth: 0,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              flex: {
                xs: "0 0 100%",
                sm: "0 0 calc((100% - 16px) / 2)",
                md: "0 0 calc((100% - 32px) / 3)",
              },
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "16 / 10",
                minHeight: 160,
                borderRadius: "6px",
                overflow: "hidden",
                border: `1px solid ${t.line}`,
                backgroundColor: t.subtle,
              }}
            >
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{ width: "100%", height: "100%" }}
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={GALLERY_CONTROL_SLOT_SX} />
    </Box>

    {/* paragraph */}
    <SkeletonParagraph widths={[100, 97, 99, 94, 98, 91, 60]} />
  </Box>
);

const PageSkeleton = ({ t }) => (
  <Box sx={LAYOUT_SX}>
    <Box
      sx={{ border: t.bd, borderRadius: 3, p: 2.25, backgroundColor: t.card }}
    >
      <Skeleton width={110} height={20} />
      <Box sx={{ borderBottom: t.bd, my: 1.5 }} />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={30}
          sx={{ mb: 1, ml: i % 3 === 0 ? 0 : 2 }}
        />
      ))}
    </Box>

    <Box
      sx={{
        border: t.bd,
        borderRadius: 3,
        backgroundColor: t.card,
        p: { xs: 2.5, md: 5 },
      }}
    >
      <Skeleton width="55%" height={56} />
      <Skeleton width="40%" height={20} />
      <Box sx={{ borderBottom: t.bd, mt: 1.5, mb: 3 }} />
<ArticleBodySkeleton t={t} />
    </Box>
  </Box>
);

// ==============================================================
// SIDEBAR (left side)
// ==============================================================

// A single clickable guide inside an option
const SideItem = ({ t, active, onClick, children }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      ml: 1.5,
      px: 1.25,
      py: 0.9,
      mb: 0.25,
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: 13.5,
      lineHeight: 1.4,
      fontWeight: active ? 600 : 400,
      color: active ? t.accent : t.text2,
      backgroundColor: "transparent",
      transition: "color 0.15s, background-color 0.15s",
      "&:hover": {
        backgroundColor: active ? "transparent" : t.subtle,
      },
      "&:focus-visible": { outline: `2px solid ${t.accent}` },
    }}
  >
    <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
      {children}
    </Box>
  </Box>
);

// The option title row (expand / collapse)
const GroupHeader = ({ t, open, onClick, children }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      px: 0.75,
      py: 0.9,
      mt: 0.5,
      borderRadius: "4px",
      cursor: "pointer",
      color: t.text,
      "&:hover": { backgroundColor: t.subtle },
      "&:focus-visible": { outline: `2px solid ${t.accent}` },
    }}
  >
    <Icon
      icon="lucide:chevron-right"
      width={15}
      style={{
        transform: open ? "rotate(90deg)" : "none",
        transition: "transform 0.15s",
        flexShrink: 0,
        color: t.muted,
      }}
    />
    <Box
      component="span"
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: 13.5,
        fontWeight: 700,
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Box>
  </Box>
);

// One option + its guides
const OptionGroup = ({
  t,
  option,
  open,
  activeGuideId,
  onToggle,
  onSelectGuide,
}) => (
  <Box>
    <GroupHeader t={t} open={open} onClick={() => onToggle(option.id)}>
      {option.name}
    </GroupHeader>

    <Collapse in={open}>
      {option.guides.length === 0 ? (
        <Typography
          variant="caption"
          sx={{ display: "block", px: 1.25, py: 0.75, color: t.muted }}
        >
          No guide present
        </Typography>
      ) : (
        option.guides.map((g) => (
          <Box key={g.id}>
            <SideItem
              t={t}
              active={g.id === activeGuideId}
              onClick={() => onSelectGuide(g.id, option.id)}
            >
              {g.title}
            </SideItem>
          </Box>
        ))
      )}
    </Collapse>
  </Box>
);

// Contents panel: header stays fixed, only the list scrolls
const HelpSidebar = ({
  t,
  options,
  openIds,
  activeGuideId,
  onToggleOption,
  onSelectGuide,
}) => (
  <Box
    sx={{
      alignSelf: { md: "start" },
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      maxHeight: { md: "60vh" },
      overflow: "hidden",
      borderRadius: 3,
      backgroundColor: t.card,
      p: 2.25,
    }}
  >
    {/* ----- FIXED HEADER ----- */}
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        pb: 1.5,
        mb: 1,
        borderBottom: t.bd,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon icon="lucide:list" width={16} color={t.muted} />
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: t.text,
          }}
        >
          Contents
        </Typography>
      </Box>
    </Box>

    {/* ----- SCROLLING OPTIONS LIST (no visible scrollbar) ----- */}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        ...HIDDEN_SCROLLBAR_SX,
      }}
    >
      {options.map((o) => (
        <OptionGroup
          key={o.id}
          t={t}
          option={o}
          open={openIds.has(o.id)}
          activeGuideId={activeGuideId}
          onToggle={onToggleOption}
          onSelectGuide={onSelectGuide}
        />
      ))}
    </Box>
  </Box>
);

// ==============================================================
// ARTICLE (right side)
// ==============================================================

// Displays guide imagery alongside the article content on wider screens.
const GuideImage = ({ t, src, alt, onZoom }) => {
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
  }, [src]);

  if (!src) return null;

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "16 / 10",
        position: "relative",
        minHeight: state === "loading" ? 160 : 0,
        borderRadius: "6px",
        overflow: "hidden",
        border: `1px solid ${t.line}`,
        backgroundColor: t.subtle,
      }}
    >
      {state === "loading" && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: "absolute", inset: 0, height: "100%" }}
        />
      )}

      {state === "error" ? (
        <Box sx={{ py: 5, textAlign: "center", color: t.muted }}>
          <Icon icon="lucide:image-off" width={26} />
          <Typography sx={{ fontSize: 12, mt: 0.5 }}>
            Image unavailable
          </Typography>
        </Box>
      ) : (
        <Box
          component="img"
          src={src}
          alt={alt}
          onLoad={() => setState("loaded")}
          onError={() => setState("error")}
          onClick={state === "loaded" ? () => onZoom(src, alt) : undefined}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            // objectFit: "contain",
                        objectFit: "cover",

            opacity: state === "loaded" ? 1 : 0,
            transition: "opacity 0.25s",
            cursor: state === "loaded" ? "zoom-in" : "default",
          }}
        />
      )}
    </Box>
  );
};

// Image + rich text of a loaded guide
const GuideContent = ({ t, guide, onZoom }) => {
  const articleSx = useMemo(() => getArticleSx(t), [t]);
  const parsed = useMemo(() => parseGuide(guide.text || ""), [guide.text]);
  const [currentPage, setCurrentPage] = useState(0);
  const images = useMemo(
    () =>
      Array.isArray(guide.images)
        ? guide.images
        : guide.image
          ? [guide.image]
          : [],
    [guide.images, guide.image],
  );
  const imagesPerPage = 3;
  const totalPages = Math.ceil(images.length / imagesPerPage);
  const startIndex = currentPage * imagesPerPage;
  const visibleImages = images.slice(startIndex, startIndex + imagesPerPage);

  useEffect(() => {
    setCurrentPage(0);
  }, [images]);

  return (
    <Box>
      {images.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 2,
            width: "100%",
            mb: 3,
          }}
        >
          {visibleImages.map((src, index) => (
            <GuideImage
              key={`${src}-${startIndex + index}`}
              t={t}
              src={src}
              alt={`${guide.title} image ${startIndex + index + 1}`}
              onZoom={onZoom}
            />
          ))}
          {totalPages > 1 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Button
                size="small"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage((page) => page - 1)}
                sx={{ textTransform: "none" }}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ color: t.muted }}>
                Page {currentPage + 1} of {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={currentPage === totalPages - 1}
                onClick={() => setCurrentPage((page) => page + 1)}
                sx={{ textTransform: "none" }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}

      {parsed.hasText ? (
<Box
  className="ql-editor"
  sx={articleSx}
  dangerouslySetInnerHTML={{ __html: parsed.html }}
/>      ) : (
        <Typography sx={{ color: t.muted, fontFamily: SERIF }}>
          This guide has no written content.
        </Typography>
      )}
    </Box>
  );
};

// Right-hand side: title, subtitle and the body (loader / error / content)
const GuideArticle = ({
  t,
  scrollRef,
  activeGuideId,
  activeOption,
  listGuide,
  detail,
  onRetry,
  onZoom,
}) => {
  const guide = detail?.status === "done" ? detail.data : null;

  const renderBody = () => {
    if (!listGuide) {
      return (
        <EmptyState
          t={t}
          icon="lucide:book-open"
          title="No guide present"
          hint="This option does not have any guides yet."
        />
      );
    }
    if (detail?.status === "error") {
      return (
        <EmptyState
          t={t}
          icon="lucide:cloud-off"
          title="Unable to load this guide"
          hint={detail.message}
          action={<RetryButton onClick={onRetry} />}
        />
      );
    }
    if (!guide) return <ArticleBodySkeleton t={t} />;
    return <GuideContent t={t} guide={guide} onZoom={onZoom} />;
  };

  return (
    <Box
      ref={scrollRef}
      sx={{
        minWidth: 0,
        minHeight: 0,
        overflowY: { md: "auto" },
        ...HIDDEN_SCROLLBAR_SX,
        borderRadius: 3,
        backgroundColor: t.card,
        p: { xs: 2.5, sm: 3.5, md: 5 },
      }}
    >
      <Box
        key={`${activeOption?.id ?? "none"}-${activeGuideId ?? "none"}`}
        sx={{ animation: `${fadeIn} 0.3s ease` }}
      >
        {/* title + subtitle come from the list, so they show instantly */}
        <Typography
          component="h1"
          sx={{
            fontFamily: SERIF,
            fontWeight: 400,
            fontSize: { xs: 24, md: 32 },
            lineHeight: 1.2,
            color: t.text,
            overflowWrap: "anywhere",
          }}
        >
          {listGuide?.title || (activeOption ? "No guide present" : "No guide selected")}
        </Typography>

        <Box
          sx={{
            mt: 0.75,
            mb: 3,
            pb: 1,
            fontSize: 13,
            color: t.muted,
            borderBottom: `1px solid ${t.line}`,
          }}
        >
          {activeOption && (
            <>
              From{" "}
              <Box component="span" sx={{ color: t.accent }}>
                {activeOption.name}
              </Box>
              {activeOption.description ? `, ${activeOption.description}` : ""}
            </>
          )}
        </Box>

        {renderBody()}
      </Box>
    </Box>
  );
};

// ==============================================================
// IMAGE VIEW MODAL
// ==============================================================
const ImageViewModal = ({ t, open, src, alt, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    PaperProps={{
      sx: {
        backgroundColor: t.card,
        backgroundImage: "none",
        borderRadius: 2,
      },
    }}
  >
    <Box sx={{ position: "relative", p: 1.5 }}>
      <IconButton
        size="small"
        aria-label="Close"
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: "#fff",
          backgroundColor: "rgba(0,0,0,0.55)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
        }}
      >
        <Icon icon="lucide:x" width={18} />
      </IconButton>
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "85vh",
          borderRadius: 1,
        }}
      />
    </Box>
  </Dialog>
);


const Help = ({ isDarkMode = false }) => {
  const t = useMemo(() => getTokens(isDarkMode), [isDarkMode]);

  // ---------- list: GET /guide/options/by-role-id ----------
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [activeGuideId, setActiveGuideId] = useState(null);
  const [activeOptionId, setActiveOptionId] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set());

  // ---------- detail: GET /guide/guides/{id} (called on every click) ----------
  const [detail, setDetail] = useState(null); // { status, data?, message? }
  const latestGuideId = useRef(null); // ignores late responses of older clicks

  // ---------- ui ----------
  const [lightbox, setLightbox] = useState({ open: false, src: "", alt: "" });

  // ---------- fixed-height layout (sidebar stays put, only the article scrolls) ----------
  const layoutRef = useRef(null);
  const articleRef = useRef(null);
  const [layoutHeight, setLayoutHeight] = useState(null);

  // fit the layout to the remaining viewport height (works under any header)
  useLayoutEffect(() => {
    if (loading || error || options.length === 0) return undefined;

    const update = () => {
      if (!layoutRef.current) return;
      const { top } = layoutRef.current.getBoundingClientRect();
      setLayoutHeight(Math.max(window.innerHeight - top - 24, 420)); // 24 = page bottom padding
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [loading, error, options.length]);

  // ---------- guide detail: always hits the API, shows loader until done ----------
  const fetchGuide = useCallback(async (id) => {
    latestGuideId.current = id;
    setDetail({ status: "loading" });

    try {
      const { data } = await axios.get(
        `${BASE_URL}/guide/guides/${id}`,
        getAuthConfig(),
      );
      if (latestGuideId.current !== id) return; // user already clicked another guide
      setDetail({ status: "done", data: mapGuide(data) });
    } catch (e) {
      if (latestGuideId.current !== id) return;
      setDetail({
        status: "error",
        message: apiErrorMessage(e, "Could not load this guide."),
      });
    }
  }, []);

  // ---------- options list ----------
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(
          `${BASE_URL}/guide/options/by-role-id`,
          getAuthConfig(),
        );
        if (cancelled) return;

        const list = mapOptions(data);
        const initialOption =
          list.find((option) => option.guides.length > 0) || list[0];
        const initialGuideId = initialOption?.guides[0]?.id ?? null;

        setOptions(list);
        setActiveGuideId(initialGuideId);
        setActiveOptionId(initialOption?.id ?? null);
        setOpenIds(new Set(initialOption ? [initialOption.id] : []));
        if (initialGuideId != null) fetchGuide(initialGuideId);
      } catch (e) {
        if (cancelled) return;
        setOptions([]);
        setError(apiErrorMessage(e, "Could not load help topics."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, fetchGuide]);

  // ---------- derived ----------
  const activeOption =
    options.find((option) => option.id === activeOptionId) || null;
  const listGuide =
    activeOption?.guides.find((item) => item.id === activeGuideId) || null;

  // ---------- handlers ----------
  const selectGuide = (id, optionId) => {
      if (id === activeGuideId) return;

    setActiveGuideId(id);
    setActiveOptionId(optionId);
    setOpenIds((p) => new Set(p).add(optionId));
    fetchGuide(id); // API call on every click, with loader
    articleRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectOption = (id) => {
    const option = options.find((item) => item.id === id);
    if (!option) return;

    const isOpen = openIds.has(id);
    setOpenIds((p) => {
      const next = new Set(p);
      if (isOpen) next.delete(id);
      else next.add(id);
      return next;
    });

    if (isOpen) return;

    if (option.guides.length === 0) {
      latestGuideId.current = null;
      setActiveGuideId(null);
      setActiveOptionId(option.id);
      setDetail(null);
    } else {
      selectGuide(option.guides[0].id, option.id);
    }
  };

  const openLightbox = (src, alt) => setLightbox({ open: true, src, alt });
  const closeLightbox = () => setLightbox((p) => ({ ...p, open: false }));

  // ---------- render ----------
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: t.card,
        minHeight: { xs: "100vh", md: "auto" },
        color: t.text,
      }}
    >
      {loading ? (
        <PageSkeleton t={t} />
      ) : error ? (
        <Box sx={{ border: t.bd, borderRadius: 3, backgroundColor: t.card }}>
          <EmptyState
            t={t}
            icon="lucide:cloud-off"
            title="Unable to load help topics"
            hint={error}
            action={<RetryButton onClick={() => setReloadKey((k) => k + 1)} />}
          />
        </Box>
      ) : options.length === 0 ? (
        <Box sx={{ border: t.bd, borderRadius: 3, backgroundColor: t.card }}>
          <EmptyState
            t={t}
            icon="lucide:book-open"
            title="No help topics available"
            hint="There are currently no options available for your role."
          />
        </Box>
      ) : (
        <Box
          ref={layoutRef}
          sx={{
            ...LAYOUT_SX,
            alignItems: { xs: "start", md: "stretch" },
            gridTemplateRows: { md: "minmax(0, 1fr)" },
            height: { md: layoutHeight ? `${layoutHeight}px` : "auto" },
          }}
        >
          <HelpSidebar
            t={t}
            options={options}
            openIds={openIds}
            activeGuideId={activeGuideId}
            onToggleOption={selectOption}
            onSelectGuide={selectGuide}
          />

          <GuideArticle
            t={t}
            scrollRef={articleRef}
            activeGuideId={activeGuideId}
            activeOption={activeOption}
            listGuide={listGuide}
            detail={detail}
            onRetry={() => fetchGuide(activeGuideId)}
            onZoom={openLightbox}
          />
        </Box>
      )}

      <ImageViewModal
        t={t}
        open={lightbox.open}
        src={lightbox.src}
        alt={lightbox.alt}
        onClose={closeLightbox}
      />
    </Box>
  );
};



export default Help;
